/**
 * Iter 144+ — Mobile Refuelling Log — Fixed & Offline-capable.
 *
 * Changes from previous version:
 * - Tank is truly OPTIONAL for all kinds including tank_refill
 * - Photos are OPTIONAL (strong encouragement but never block submit)
 * - GPS is captured at the moment each photo is taken (not on page load)
 * - Offline-first: if network fails, logs saved to localStorage and synced when online
 * - No liveness check on photo capture (just camera capture — fast at petrol pump)
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, FilePlus2, Send, Loader2, MapPin, Camera, Check, X, WifiOff, RefreshCw,
} from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { enqueue, queueCount, syncQueue } from '../../lib/offlineQueue';

// Local-time datetime string (not UTC) for datetime-local input
function localDatetimeNow() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Quick non-blocking GPS grab (5s timeout, fail open)
async function quickGPS() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const t = setTimeout(() => resolve(null), 5000);
    navigator.geolocation.getCurrentPosition(
      (p) => { clearTimeout(t); resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); },
      () => { clearTimeout(t); resolve(null); },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 },
    );
  });
}

// localDatetimeNow and quickGPS helpers below

const FUEL_KINDS = [
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'tank_refill', label: 'Tank Refill' },
];

const PAYMENT_MODES = ['Fuel Card', 'Cash', 'UPI', 'Credit Account'];

// Photo steps — all OPTIONAL (encouragement only, never block submit)
const PHOTO_STEPS = [
  { slot: 'meter_zero', label: 'Pump meter at ZERO',
    hint: 'Pump display showing 0 BEFORE filling.' },
  { slot: 'vehicle', label: 'Vehicle being fuelled',
    hint: 'Whole vehicle including number plate.' },
  { slot: 'meter_final', label: 'Final meter reading',
    hint: 'Final pump display with amount.' },
  { slot: 'pump_bill', label: 'Pump bill / receipt',
    hint: 'Lay bill flat, capture entire receipt.' },
];

// Compress + base64-encode a captured image client-side so the JSON payload
// stays manageable. Max edge 1600 px / JPEG q ≈ 0.75 mirrors the server-side
// retention envelope, so the round-trip stays cheap.
async function fileToCompressedBase64(file, maxDim = 1600, quality = 0.75) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  // strip the "data:image/jpeg;base64," prefix — backend tolerates both
  return dataUrl.split(',', 2)[1];
}

export default function MobileFuelLog() {
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [drivers, setDrivers] = useState([]);          // driver pool
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDrop, setShowDriverDrop] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState({});
  const [offlineCount, setOfflineCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [gpsCaptured, setGpsCaptured] = useState(false);
  const [phase, setPhase] = useState('log_in');       // 'log_in' | 'log_out'
  const [logInRecord, setLogInRecord] = useState(null); // saved after LOG IN
  const fileRefs = useRef({ meter_zero: null, vehicle: null, meter_final: null, pump_bill: null, driver_selfie: null });
  const [form, setForm] = useState({
    kind: 'vehicle',
    vehicle_id: '',
    equipment_id: '',
    tank_id: '',       // required for tank_refill, optional otherwise
    driver_name: '',
    log_datetime: localDatetimeNow(),
    fuel_station: '',
    fuel_type: '',
    fuel_quantity_l: 0,
    rate_per_l: 0,
    total_amount: 0,
    odometer_reading: '',
    hour_meter_reading: '',
    payment_mode: 'Fuel Card',
    gps_lat: null,
    gps_lng: null,
    remarks: '',
    // Prompt 1
    bill_number: '', bill_date: '', grn_number: '',
    opening_balance_l: '', closing_balance_l: '',
    // Prompt 2
    start_meter_reading: '', start_meter_time: '',
    end_meter_reading: '', end_meter_time: '',
    idle_hours: 0, breakdown_hours: 0,
    // Prompt 3
    work_description: '', work_quantity: '', work_unit: '',
    // Prompt 4
    issue_slip_number: '',
    issue_slip_no: '',     // equipment kind: required issue slip (Change 1)
    // Prompt 5
    excess_reason: '', excess_reason_confirmed: false,
  });

  useEffect(() => {
    apiGet('/fuel/vehicles').then((d) => setVehicles(d?.items || [])).catch(() => {});
    apiGet('/fuel/equipment').then((d) => setEquipment(d?.items || [])).catch(() => {});
    apiGet('/fuel/tanks').then((d) => setTanks(d?.items || [])).catch(() => {});
    apiGet('/fuel/drivers', { names: 'true' }).then((d) => setDrivers(d?.drivers || [])).catch(() => {});
    queueCount().then(setOfflineCount).catch(() => {});
  }, []);

  // Filter tanks to equipment's project when equipment is selected
  const projectTanks = useMemo(() => {
    if (form.kind !== 'equipment' || !form.equipment_id) return tanks;
    const eq = equipment.find(e => e.id === form.equipment_id || e.eq_id === form.equipment_id);
    const eqProjectId = eq?.project_id;
    if (!eqProjectId) return tanks;
    const filtered = tanks.filter(t => t.project_id === eqProjectId || !t.project_id);
    return filtered.length > 0 ? filtered : tanks;
  }, [tanks, equipment, form.equipment_id, form.kind]);
  const filteredDrivers = driverSearch
    ? drivers.filter(d => d.toLowerCase().includes(driverSearch.toLowerCase()))
    : drivers;

  const set = (k, v) => setForm((f) => {
    const nf = { ...f, [k]: v };
    if (k === 'fuel_quantity_l' || k === 'rate_per_l') {
      const q = Number(k === 'fuel_quantity_l' ? v : nf.fuel_quantity_l);
      const r = Number(k === 'rate_per_l' ? v : nf.rate_per_l);
      if (q && r) nf.total_amount = Number((q * r).toFixed(2));
    }
    // Auto-fill fuel_type when vehicle or equipment is selected
    if (k === 'vehicle_id') {
      const veh = vehicles.find(vv => vv.id === v);
      if (veh?.fuel_type) nf.fuel_type = veh.fuel_type;
    }
    if (k === 'equipment_id') {
      const eq = equipment.find(e => e.id === v);
      if (eq?.fuel_type) nf.fuel_type = eq.fuel_type;
      if (eq?.output_unit && !nf.work_unit) nf.work_unit = eq.output_unit;
    }
    return nf;
  });

  // Auto-fill opening_balance_l from previous log
  useEffect(() => {
    if (form.kind === 'vehicle' && form.vehicle_id) {
      apiGet(`/fuel/logs/prev-closing-balance?vehicle_id=${form.vehicle_id}`)
        .then(d => { if (d.closing_balance_l != null) setForm(f => ({ ...f, opening_balance_l: d.closing_balance_l })); })
        .catch(() => {});
    } else if (form.kind === 'equipment' && form.equipment_id) {
      apiGet(`/fuel/logs/prev-closing-balance?equipment_id=${form.equipment_id}`)
        .then(d => { if (d.closing_balance_l != null) setForm(f => ({ ...f, opening_balance_l: d.closing_balance_l })); })
        .catch(() => {});
    }
  }, [form.vehicle_id, form.equipment_id, form.kind]); // eslint-disable-line

  // Reset phase when kind changes
  useEffect(() => {
    setPhase('log_in');
    setLogInRecord(null);
  }, [form.kind]); // eslint-disable-line

  const capturedCount = PHOTO_STEPS.reduce((n, s) => n + (photos[s.slot] ? 1 : 0), 0);
  const photosRequired = form.kind === 'vehicle' || form.kind === 'equipment';
  const allPhotosCaptured = !photosRequired || capturedCount === PHOTO_STEPS.length;

  const onPick = async (slot, file) => {
    if (!file) return;
    // GPS: capture once on the very first photo — not on every photo.
    // The first photo is taken at the pump, which is the location we want.
    // Subsequent photos are taken at the same location so re-capturing wastes time.
    let gpsForPhoto = null;
    if (!gpsCaptured && !form.gps_lat) {
      gpsForPhoto = await quickGPS();
      if (gpsForPhoto) {
        setGpsCaptured(true);
        setForm(f => ({ ...f, gps_lat: gpsForPhoto.lat, gps_lng: gpsForPhoto.lng }));
      }
    }
    try {
      const base64 = await fileToCompressedBase64(file);
      if (!base64) { toast.error('Could not read photo — try again'); return; }
      const preview = URL.createObjectURL(file);
      setPhotos((p) => {
        if (p[slot]?.preview) URL.revokeObjectURL(p[slot].preview);
        return { ...p, [slot]: { preview, base64 } };
      });
    } catch {
      toast.error('Could not read photo — try again');
    }
  };

  const clearPhoto = (slot) => setPhotos((p) => {
    if (p[slot]?.preview) URL.revokeObjectURL(p[slot].preview);
    const nx = { ...p }; delete nx[slot]; return nx;
  });

  // Offline sync via lib/offlineQueue.js (IndexedDB — handles large base64 photos safely)
  const syncOfflineQueue = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await syncQueue();
      const remaining = await queueCount();
      setOfflineCount(remaining);
      if (result.synced > 0) toast.success(`${result.synced} offline fuel log(s) synced`);
      if (remaining > 0) toast.error(`${remaining} log(s) still pending — check connection`);
    } catch {
      toast.error('Sync failed — check connection');
    } finally { setSyncing(false); }
  }, []);

  const buildPayload = () => {
    const p = {
      ...form,
      log_datetime: new Date(form.log_datetime).toISOString(),
      fuel_quantity_l: Number(form.fuel_quantity_l || 0),
      rate_per_l: Number(form.rate_per_l || 0),
      total_amount: Number(form.total_amount || 0),
      odometer_reading: form.odometer_reading ? Number(form.odometer_reading) : null,
      hour_meter_reading: form.hour_meter_reading ? Number(form.hour_meter_reading) : null,
      vehicle_id: form.kind === 'vehicle' ? form.vehicle_id || null : null,
      equipment_id: form.kind === 'equipment' ? form.equipment_id || null : null,
      tank_id: form.issue_slip_number ? null : (form.tank_id || null), // skip if issue slip already deducted
      // Prompt 1
      opening_balance_l: form.opening_balance_l !== '' ? parseFloat(form.opening_balance_l) : null,
      closing_balance_l: form.closing_balance_l !== '' ? parseFloat(form.closing_balance_l) : null,
      // Prompt 2
      start_meter_reading: form.start_meter_reading !== '' ? parseFloat(form.start_meter_reading) : null,
      end_meter_reading: form.end_meter_reading !== '' ? parseFloat(form.end_meter_reading) : null,
      start_meter_time: form.start_meter_time ? new Date(form.start_meter_time).toISOString() : null,
      end_meter_time: form.end_meter_time ? new Date(form.end_meter_time).toISOString() : null,
      idle_hours: parseFloat(form.idle_hours || 0),
      breakdown_hours: parseFloat(form.breakdown_hours || 0),
      // Prompt 3
      work_quantity: form.work_quantity !== '' ? parseFloat(form.work_quantity) : null,
    };
    // Photos are OPTIONAL — include whatever was captured
    PHOTO_STEPS.forEach(({ slot }) => {
      if (photos[slot]?.base64) {
        p[`${slot}_photo_base64`] = photos[slot].base64;
        p[`${slot}_photo_mime`] = 'image/jpeg';
      }
    });
    if (photos.driver_selfie?.base64) {
      p.driver_selfie_base64 = photos.driver_selfie.base64;
      p.driver_selfie_mime = 'image/jpeg';
    }
    return p;
  };

  const handleLogIn = async () => {
    if (form.kind === 'equipment' && !form.equipment_id) return toast.error('Select equipment');
    if (form.kind === 'vehicle' && !form.vehicle_id) return toast.error('Select vehicle');
    if (!form.fuel_quantity_l || Number(form.fuel_quantity_l) <= 0) return toast.error('Enter fuel quantity');
    if (form.kind === 'equipment' && !form.issue_slip_no?.trim()) return toast.error('Issue slip number is required for equipment refuelling');
    if (photosRequired && !allPhotosCaptured) {
      const missing = PHOTO_STEPS.filter(s => !photos[s.slot]).map(s => s.label).join(', ');
      return toast.error(`Capture all 4 photos first. Missing: ${missing}`);
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      payload.event_type = 'log_in';
      payload.issue_slip_number = form.kind === 'equipment' ? (form.issue_slip_no || null) : null;
      const saved = await apiPost('/fuel/logs', payload);
      if (saved?.rate_warning) {
        setSubmitting(false);
        if (window.confirm(saved.rate_warning)) {
          const confirmed = buildPayload();
          confirmed.event_type = 'log_in';
          confirmed.rate_confirmed = true;
          confirmed.issue_slip_number = payload.issue_slip_number;
          const saved2 = await apiPost('/fuel/logs', confirmed);
          setLogInRecord(saved2);
          setPhase('log_out');
          toast.success('Logged in — fill LOG OUT details when finished');
        }
        return;
      }
      setLogInRecord(saved);
      setPhase('log_out');
      toast.success('Logged in — fill LOG OUT details when finished');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Log in failed');
    } finally { setSubmitting(false); }
  };

  const handleLogOut = async () => {
    if (!logInRecord?.id) return toast.error('No log-in record found');
    setSubmitting(true);
    try {
      await apiPut(`/fuel/logs/${logInRecord.id}`, {
        event_type: 'log_out',
        end_meter_reading: form.end_meter_reading !== '' ? Number(form.end_meter_reading) : null,
        closing_balance_l: form.closing_balance_l !== '' ? Number(form.closing_balance_l) : null,
        idle_hours: Number(form.idle_hours || 0),
        breakdown_hours: Number(form.breakdown_hours || 0),
        work_description: form.work_description || '',
        work_quantity: form.work_quantity !== '' ? Number(form.work_quantity) : null,
        work_unit: form.work_unit || '',
        remarks: form.remarks || '',
      });
      toast.success('Fuel log completed');
      nav('/m/fuel');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Log out failed');
    } finally { setSubmitting(false); }
  };

  const submit = async (rateConfirmed = false, excessConfirmed = false) => {
    if (form.kind === 'vehicle' && !form.vehicle_id) return toast.error('Pick a vehicle');
    if (form.kind === 'equipment' && !form.equipment_id) return toast.error('Pick an equipment');
    if (form.kind === 'tank_refill' && !form.tank_id) return toast.error('Select the tank being refilled');
    if (!form.fuel_quantity_l || !form.rate_per_l) return toast.error('Quantity & rate required');
    if (photosRequired && !allPhotosCaptured) {
      const missing = PHOTO_STEPS.filter(s => !photos[s.slot]).map(s => s.label).join(', ');
      return toast.error(`Capture all 4 photos first. Missing: ${missing}`);
    }
    setSubmitting(true);
    const payload = { ...buildPayload(), rate_confirmed: rateConfirmed, excess_reason_confirmed: excessConfirmed };
    try {
      const res = await apiPost('/fuel/logs', payload);
      if (res?.rate_warning) {
        setSubmitting(false);
        if (window.confirm(res.rate_warning)) return submit(true, excessConfirmed);
        return;
      }
      if (res?.excess_warning) {
        setSubmitting(false);
        const reason = window.prompt(`${res.excess_warning}\n\nEnter reason for excess consumption:`);
        if (!reason) return;
        setForm(f => ({ ...f, excess_reason: reason }));
        const updatedPayload = { ...payload, excess_reason: reason, excess_reason_confirmed: true };
        setSubmitting(true);
        try {
          const res2 = await apiPost('/fuel/logs', updatedPayload);
          if (res2?.daily_total_warning) toast.warning(res2.daily_total_warning, { duration: 6000 });
          toast.success('Fuel expense submitted.');
          nav('/m/fuel');
        } catch (e2) {
          toast.error(e2?.response?.data?.detail?.message || 'Submit failed');
        } finally { setSubmitting(false); }
        return;
      }
      if (res?.daily_total_warning) toast.warning(res.daily_total_warning, { duration: 6000 });
      toast.success('Fuel expense submitted.');
      nav('/m/fuel');
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Check if it's a network/offline error (no response)
      if (!e?.response) {
        // Network failure — save to IndexedDB (offlineQueue handles large base64 safely)
        try {
          await enqueue('fuel', {
            label: `Fuel Log — ${form.kind} · ${form.fuel_quantity_l}L`,
            payload,
          });
          const count = await queueCount();
          setOfflineCount(count);
          setSubmitting(false);
          toast.success(`Saved offline — will sync when connected (${count} pending)`);
          nav('/m/fuel');
        } catch (qErr) {
          // enqueue itself failed (very unusual) — tell user explicitly
          toast.error('Could not save offline. Please retry when connected.');
          setSubmitting(false);
        }
        return;
      }
      if (d?.validation) {
        const failed = d.validation.filter(c => c.status === 'fail');
        const msg = failed.length
          ? failed.map(c => `${c.name}: ${c.detail}`).join('\n')
          : (d.message || 'Validation failed');
        toast.error(msg, { duration: 8000 });
      } else {
        toast.error(typeof d === 'string' ? d : (d?.message || 'Submit failed'));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-40" data-testid="m-fuel-log">
      <div className="bg-[#3b4872] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m/fuel" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div>
          <div className="font-display font-bold flex items-center gap-1.5"><FilePlus2 size={14} /> Refuelling Log</div>
          <div className="text-[10px] text-slate-300">Capture a fuel entry</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {form.gps_lat && <span className="text-[10px] flex items-center gap-1 text-emerald-300"><MapPin size={11} /> GPS</span>}
          {offlineCount > 0 && (
            <button onClick={syncOfflineQueue} disabled={syncing}
              className="text-[10px] flex items-center gap-1 text-amber-300 bg-amber-400/20 px-2 py-0.5 rounded"
              data-testid="fuel-sync-offline-btn">
              {syncing ? <RefreshCw size={10} className="animate-spin" /> : <WifiOff size={10} />}
              {offlineCount} pending
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <Section label="Kind">
          <div className="grid grid-cols-3 gap-1.5">
            {FUEL_KINDS.map((k) => (
              <button
                key={k.value}
                onClick={() => set('kind', k.value)}
                data-testid={`m-fuel-kind-${k.value}`}
                className={`px-2 py-2 rounded-sm text-xs font-display font-bold border ${
                  form.kind === k.value ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-700 border-slate-300'
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </Section>

        {form.kind === 'vehicle' && (
          <Section label="Vehicle">
            <select value={form.vehicle_id} onChange={(e) => set('vehicle_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="m-fuel-vehicle">
              <option value="">Select vehicle…</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {`${v.vehicle_number} · ${v.vehicle_type}`}
                  {(v.project_name || v.office_location_name) ? ` (${v.project_name || v.office_location_name})` : ''}
                </option>
              ))}
            </select>
          </Section>
        )}

        {form.kind === 'equipment' && (
          <Section label="Equipment">
            <select value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="m-fuel-equipment">
              <option value="">Select equipment…</option>
              {equipment.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {`${eq.equipment_id} · ${eq.equipment_type}`}
                  {(eq.project_name || eq.office_location_name) ? ` (${eq.project_name || eq.office_location_name})` : ''}
                </option>
              ))}
            </select>
          </Section>
        )}

        {/* Tank — REQUIRED for tank_refill, OPTIONAL for equipment */}
        {form.kind === 'tank_refill' ? (
          <Section label="Tank Being Refilled *">
            <select value={form.tank_id} onChange={(e) => set('tank_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="m-fuel-tank">
              <option value="">— Select tank —</option>
              {tanks.map((t) => <option key={t.id} value={t.id}>{`${t.tank_id} · ${Math.round(t.current_level_l ?? 0)}L of ${Math.round(t.capacity_l ?? 0)}L`}</option>)}
            </select>
          </Section>
        ) : form.kind === 'equipment' ? (
          <Section label="Fuel Source Tank (select to deduct from tank stock)">
            <select value={form.tank_id} onChange={(e) => set('tank_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="m-fuel-tank-equipment">
              <option value="">— No tank / direct pump —</option>
              {projectTanks.map((t) => <option key={t.id} value={t.id}>{`${t.tank_id} · ${Math.round(t.current_level_l ?? 0)}L available`}</option>)}
            </select>
            {form.tank_id && (
              <p className="text-[11px] text-amber-700 mt-1.5 font-medium">
                Fuel will be deducted from the selected tank when submitted.
              </p>
            )}
            {form.tank_id && form.fuel_quantity_l && (() => {
              const selectedTank = projectTanks.find(t => t.id === form.tank_id);
              if (!selectedTank) return null;
              const remaining = (selectedTank.current_level_l ?? 0) - parseFloat(form.fuel_quantity_l || 0);
              return (
                <div className={`mt-2 text-[11px] font-semibold px-3 py-1.5 rounded ${remaining < 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                  {remaining < 0
                    ? `Insufficient stock — only ${Math.round(selectedTank.current_level_l)}L available`
                    : `After refuelling: ${Math.round(remaining)}L will remain in tank`}
                </div>
              );
            })()}
          </Section>
        ) : null}

        <Section label="Date &amp; Time">
          <input type="datetime-local" value={form.log_datetime} onChange={(e) => set('log_datetime', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
        </Section>

        {/* Fuel type is auto-read from the selected vehicle/equipment — not editable */}
        {form.fuel_type && (
          <Section label="Fuel Type (from vehicle)">
            <div className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-sm text-sm text-slate-700 font-medium" data-testid="m-fuel-type-display">
              {form.fuel_type}
            </div>
          </Section>
        )}

        <div className="grid grid-cols-3 gap-2">
          <Section label="Qty (L)">
            <input type="number" inputMode="decimal" value={form.fuel_quantity_l} onChange={(e) => set('fuel_quantity_l', e.target.value)} className="w-full px-2 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="m-fuel-qty" />
          </Section>
          <Section label="Rate ₹/L">
            <input type="number" inputMode="decimal" value={form.rate_per_l} onChange={(e) => set('rate_per_l', e.target.value)} className="w-full px-2 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="m-fuel-rate" />
          </Section>
          <Section label="Total ₹">
            <input type="number" inputMode="decimal" value={form.total_amount} onChange={(e) => set('total_amount', e.target.value)} className="w-full px-2 py-2.5 border border-slate-300 rounded-sm text-sm font-display font-bold" data-testid="m-fuel-total" />
          </Section>
        </div>

        {/* Change 1 — Vehicle: no Odometer; Equipment: no Hour Meter; GRN/Issue Slip per kind */}

        {/* Bill No. + Bill Date for vehicle and equipment only */}
        {(form.kind === 'vehicle' || form.kind === 'equipment') && (
          <div className="grid grid-cols-2 gap-2">
            <Section label="Bill No.">
              <input value={form.bill_number} onChange={e => set('bill_number', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="e.g. BILL-001" data-testid="m-fuel-bill-no" />
            </Section>
            <Section label="Bill Date">
              <input type="date" value={form.bill_date} onChange={e => set('bill_date', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="m-fuel-bill-date" />
            </Section>
          </div>
        )}

        {/* Equipment kind: Issue Slip (required) instead of GRN */}
        {form.kind === 'equipment' && (
          <Section label="Issue Slip No. (required)">
            <input
              value={form.issue_slip_no}
              onChange={e => set('issue_slip_no', e.target.value)}
              placeholder="Enter issue slip number"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
              data-testid="m-fuel-issue-slip"
            />
          </Section>
        )}

        {/* Tank Refill kind: GRN only */}
        {form.kind === 'tank_refill' && (
          <Section label="GRN No. (if from store)">
            <input value={form.grn_number} onChange={e => set('grn_number', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="GRN number" data-testid="m-fuel-grn" />
          </Section>
        )}

        {/* In-machine fuel balance for vehicle and equipment */}
        {(form.kind === 'vehicle' || form.kind === 'equipment') && (
          <div className="grid grid-cols-2 gap-2">
            <Section label="Opening Balance (L)">
              <input type="number" inputMode="decimal" value={form.opening_balance_l} onChange={e => set('opening_balance_l', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="Auto-filled" data-testid="m-fuel-opening-balance" />
            </Section>
            <Section label="Closing Balance (L)">
              <input type="number" inputMode="decimal" value={form.closing_balance_l} onChange={e => set('closing_balance_l', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="Remaining in machine" data-testid="m-fuel-closing-balance" />
            </Section>
          </div>
        )}

        {/* Prompt 2 — Shift meter readings */}
        {form.kind !== 'tank_refill' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Section label="Start Meter Reading">
                <input type="number" inputMode="decimal" value={form.start_meter_reading} onChange={e => set('start_meter_reading', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="e.g. 2413" data-testid="m-fuel-start-meter" />
              </Section>
              <Section label="End Meter Reading">
                <input type="number" inputMode="decimal" value={form.end_meter_reading} onChange={e => set('end_meter_reading', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="e.g. 2430" data-testid="m-fuel-end-meter" />
              </Section>
            </div>
            {(() => {
              const s = parseFloat(form.start_meter_reading);
              const e = parseFloat(form.end_meter_reading);
              if (!isNaN(s) && !isNaN(e) && e > s) {
                return <div className="text-xs text-emerald-700 font-medium px-1">Running hours: {(e - s).toFixed(2)} hrs</div>;
              }
              return null;
            })()}
            <div className="grid grid-cols-2 gap-2">
              <Section label="Idle Time (hrs)">
                <input type="number" inputMode="decimal" min="0" value={form.idle_hours} onChange={e => set('idle_hours', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="m-fuel-idle-hours" />
              </Section>
              <Section label="Breakdown Time (hrs)">
                <input type="number" inputMode="decimal" min="0" value={form.breakdown_hours} onChange={e => set('breakdown_hours', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="m-fuel-breakdown-hours" />
              </Section>
            </div>
          </>
        )}

        {/* Prompt 3 — Work done (equipment only) */}
        {form.kind === 'equipment' && (
          <>
            <Section label="Nature of Work Done">
              <input value={form.work_description} onChange={e => set('work_description', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="e.g. Excavation, rock breaking" data-testid="m-fuel-work-desc" />
            </Section>
            <div className="grid grid-cols-2 gap-2">
              <Section label="Quantity">
                <input type="number" inputMode="decimal" value={form.work_quantity} onChange={e => set('work_quantity', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="e.g. 15" data-testid="m-fuel-work-qty" />
              </Section>
              <Section label="Unit">
                <input value={form.work_unit} onChange={e => set('work_unit', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" placeholder="e.g. cum" data-testid="m-fuel-work-unit" />
              </Section>
            </div>
          </>
        )}

        <Section label="Driver (who is filling fuel now)">
          <div className="relative">
            <input
              value={driverSearch || form.driver_name}
              onChange={(e) => { setDriverSearch(e.target.value); set('driver_name', e.target.value); setShowDriverDrop(true); }}
              onFocus={() => setShowDriverDrop(true)}
              placeholder="Search or type driver name…"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
              data-testid="m-fuel-driver"
            />
            {showDriverDrop && filteredDrivers.length > 0 && (
              <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-y-auto mt-0.5">
                {filteredDrivers.map(d => (
                  <button key={d} type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-50"
                    onMouseDown={() => { set('driver_name', d); setDriverSearch(''); setShowDriverDrop(false); }}
                    data-testid={`m-fuel-driver-opt-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Section>

        <Section label="Fuel Station">
          <input value={form.fuel_station} onChange={(e) => set('fuel_station', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
        </Section>

        <Section label="Payment Mode">
          <select value={form.payment_mode} onChange={(e) => set('payment_mode', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
            {PAYMENT_MODES.map((p) => <option key={p}>{p}</option>)}
          </select>
        </Section>

        {/* Photos section — OPTIONAL, encouraged but never blocking */}
        {(form.kind === 'vehicle' || form.kind === 'equipment') && (
          <Section label={photosRequired ? `Photos ${capturedCount}/4 (required for vehicle/equipment)` : `Photos ${capturedCount}/4 (optional for tank refill)`}>
            <div className="space-y-2" data-testid="m-fuel-photo-steps">
              {PHOTO_STEPS.map((step, idx) => {
                const captured = photos[step.slot];
                return (
                  <div key={step.slot} className={`bg-white rounded-sm border p-2 ${captured ? 'border-emerald-400' : 'border-slate-300'}`}>
                    <div className="flex items-start gap-2">
                      <div className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${captured ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                        {captured ? <Check size={14} /> : idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-display font-bold">{`Photo ${idx + 1} of 4 — ${step.label}`}</div>
                        <div className="text-[10px] text-slate-500 leading-snug mt-0.5">{step.hint}</div>
                      </div>
                    </div>
                    <input
                      ref={(el) => { fileRefs.current[step.slot] = el; }}
                      hidden
                      type="file"
                      accept="image/*"
                      capture="environment"
                      data-testid={`m-fuel-photo-input-${step.slot}`}
                      onChange={(e) => onPick(step.slot, e.target.files?.[0])}
                    />
                    {captured ? (
                      <div className="mt-2 relative">
                        <img src={captured.preview} alt={step.label} className="w-full h-40 object-cover rounded-sm" />
                        <button
                          onClick={() => clearPhoto(step.slot)}
                          className="absolute top-1.5 right-1.5 bg-white/95 text-slate-800 rounded-full p-1 shadow"
                          data-testid={`m-fuel-photo-retake-${step.slot}`}
                          aria-label={`Retake ${step.label}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileRefs.current[step.slot]?.click()}
                        className="mt-2 w-full bg-slate-50 border-2 border-dashed border-slate-300 rounded-sm p-3 text-center"
                        data-testid={`m-fuel-photo-capture-${step.slot}`}
                      >
                        <Camera size={18} className="mx-auto text-slate-400 mb-1" />
                        <div className="text-[11px] font-display font-bold">Tap to open camera</div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        <Section label="Remarks">
          <textarea rows={2} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
        </Section>

        {/* FIX 8 — optional 5th photo: driver selfie */}
        <Section label="Driver Selfie (optional)">
          <input
            ref={(el) => { fileRefs.current.driver_selfie = el; }}
            hidden type="file" accept="image/*" capture="user"
            data-testid="m-fuel-photo-input-driver_selfie"
            onChange={(e) => onPick('driver_selfie', e.target.files?.[0])}
          />
          {photos.driver_selfie ? (
            <div className="relative">
              <img src={photos.driver_selfie.preview} alt="Driver selfie" className="w-full h-40 object-cover rounded-sm" />
              <button onClick={() => clearPhoto('driver_selfie')} className="absolute top-1.5 right-1.5 bg-white/95 text-slate-800 rounded-full p-1 shadow" data-testid="m-fuel-photo-retake-driver_selfie">
                <X size={14} />
              </button>
            </div>
          ) : (
            <button onClick={() => fileRefs.current.driver_selfie?.click()}
              className="w-full bg-white border-2 border-dashed border-slate-300 rounded-sm p-3 text-center"
              data-testid="m-fuel-photo-capture-driver_selfie">
              <Camera size={18} className="mx-auto text-slate-400 mb-1" />
              <div className="text-[11px] font-display font-bold">Add driver selfie (optional)</div>
            </button>
          )}
        </Section>

        {/* Change 2 — LOG OUT section (phase=log_out, equipment/vehicle only) */}
        {(form.kind === 'equipment' || form.kind === 'vehicle') && phase === 'log_out' && (
          <div className="space-y-3">
            <div className="px-1 py-2 bg-amber-50 border border-amber-200 rounded text-xs font-display font-bold uppercase tracking-widest text-amber-800">
              LOG OUT — Record Work Done
            </div>
            <Section label="End Meter Reading">
              <input type="number" inputMode="decimal" value={form.end_meter_reading}
                onChange={(e) => {
                  const endVal = e.target.value;
                  set('end_meter_reading', endVal);
                  // Auto-calculate running hours display only
                }}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                data-testid="m-fuel-end-meter" />
            </Section>
            {form.start_meter_reading && form.end_meter_reading && Number(form.end_meter_reading) > Number(form.start_meter_reading) && (
              <div className="mx-1 text-xs text-emerald-700 font-medium">
                Running: {(Number(form.end_meter_reading) - Number(form.start_meter_reading)).toFixed(2)} hrs
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Section label="Idle Time (hrs)">
                <input type="number" inputMode="decimal" min="0" value={form.idle_hours}
                  onChange={e => set('idle_hours', e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                  data-testid="m-fuel-idle-time" />
              </Section>
              <Section label="Breakdown Time (hrs)">
                <input type="number" inputMode="decimal" min="0" value={form.breakdown_hours}
                  onChange={e => set('breakdown_hours', e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                  data-testid="m-fuel-breakdown-time" />
              </Section>
            </div>
            <Section label="Nature of Work Done">
              <input value={form.work_description}
                onChange={e => set('work_description', e.target.value)}
                placeholder="e.g. Excavation, rock breaking, concrete pouring"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                data-testid="m-fuel-work-nature" />
            </Section>
            <div className="grid grid-cols-2 gap-2">
              <Section label="Quantity">
                <input type="number" inputMode="decimal" value={form.work_quantity}
                  onChange={e => set('work_quantity', e.target.value)}
                  placeholder="e.g. 15"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                  data-testid="m-fuel-work-qty" />
              </Section>
              <Section label="Unit">
                <input value={form.work_unit}
                  onChange={e => set('work_unit', e.target.value)}
                  placeholder="e.g. cum"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                  data-testid="m-fuel-work-unit" />
              </Section>
            </div>
          </div>
        )}
      </div>

      {/* Offline banner */}
      {offlineCount > 0 && (
        <div className="fixed inset-x-0 bg-amber-500/90 text-white text-xs px-4 py-2 z-40 flex items-center justify-between" style={{ bottom: 'calc(8rem + env(safe-area-inset-bottom))' }} data-testid="fuel-offline-banner">
          <span className="flex items-center gap-1.5"><WifiOff size={12} /> {offlineCount} fuel log(s) saved offline</span>
          <button onClick={syncOfflineQueue} className="font-bold underline" disabled={syncing}>{syncing ? 'Syncing…' : 'Sync now'}</button>
        </div>
      )}

      {/* Sticky action button — LOG IN or LOG OUT for equipment/vehicle; normal Submit for tank_refill */}
      <div
        className="fixed inset-x-0 bg-white border-t border-slate-200 px-4 py-3 z-50"
        style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
      >
        {form.kind === 'tank_refill' ? (
          // Tank refill: normal submit
          <button
            onClick={() => submit()}
            disabled={submitting}
            className="w-full bg-[#3b4872] hover:bg-[#2d3856] disabled:opacity-50 text-white font-display font-bold rounded-sm py-3 flex items-center justify-center gap-2"
            data-testid="m-fuel-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {submitting ? 'Submitting…' : 'Submit Tank Refill'}
          </button>
        ) : phase === 'log_in' ? (
          // Equipment/Vehicle LOG IN button
          <>
            {capturedCount > 0 && capturedCount < PHOTO_STEPS.length && (
              <p className="text-[10px] text-slate-400 text-center mb-1.5">
                {capturedCount}/{PHOTO_STEPS.length} photos captured
              </p>
            )}
            <button
              onClick={handleLogIn}
              disabled={submitting || (photosRequired && !allPhotosCaptured)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-display font-bold rounded-sm py-3 flex items-center justify-center gap-2"
              data-testid="m-fuel-log-in-btn"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {submitting
                ? 'Saving…'
                : (photosRequired && !allPhotosCaptured)
                  ? `Capture all 4 photos (${capturedCount}/4)`
                  : 'LOG IN — Record Fuelling'}
            </button>
          </>
        ) : (
          // LOG OUT button
          <button
            onClick={handleLogOut}
            disabled={submitting}
            className="w-full bg-[#3b4872] hover:bg-[#2d3856] disabled:opacity-50 text-white font-display font-bold rounded-sm py-3 flex items-center justify-center gap-2"
            data-testid="m-fuel-log-out-btn"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {submitting ? 'Saving…' : 'LOG OUT — Submit Complete Entry'}
          </button>
        )}
      </div>
    </div>
  );
}

const Section = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-display text-slate-500 px-1">{label}</label>
    {children}
  </div>
);
